import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ReportTargetType = "user" | "ad";

interface SubmitReportParams {
  targetType: ReportTargetType;
  targetId: string;
  reason: string;
  details?: string;
}

export function useReport() {
  const { user } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({ targetType, targetId, reason, details }: SubmitReportParams) => {
      if (!user) throw new Error("not_authenticated");

      // Guard: self-reporting is never allowed, regardless of how the mutation is called.
      if (targetType === "user" && targetId === user.id) {
        throw new Error("self_report");
      }

      const { error } = await supabase.from("reports").insert({
        report_type: targetType,
        // reporter_user_id is always the authenticated user — never taken from params.
        reporter_user_id: user.id,
        target_id: targetId,
        target_type: targetType,
        reason,
        details: details?.trim() || null,
        status: "open",
      } as any);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Signalement envoyé", {
        description: "Notre équipe examinera ce signalement dans les meilleurs délais.",
      });
    },
    onError: (err: any) => {
      if (err.message === "not_authenticated") {
        toast.error("Connexion requise pour signaler");
      } else if (err.message === "self_report") {
        toast.error("Vous ne pouvez pas vous signaler vous-même.");
      } else {
        toast.error("Erreur lors de l'envoi du signalement");
      }
    },
  });

  return mutation;
}
